/// <amd-module name="Repos/Tfvc/ChangesetsList/Components/ChangesetsTable" />
import * as React from "react";
import { IChangeset, IChangesetsTableProps } from "Repos/Common/Contracts/History";
import { getChangeTypeDisplayName } from "Repos/Common/Util/ChangeType";
import { getShortCommitComment } from "Repos/Common/Util/Commit";
import * as Resources from "Repos/Tfvc/ChangesetsList/Resources";
import { ObservableValue } from "VSS/Core/Observable";
import { onClickFPS } from "VSS/Platform/FPS";
import { Components, css, VssComponent } from "VSS/Platform/Layout";
import { Ago } from "VSSUI/Ago";
import { Observer } from "VSSUI/Observer";
import { Persona, PersonaSize } from "VSSUI/Persona";
import { ITableColumn, ITableRow, ITableRowDetails, SimpleTableCell, Table, TableColumnLayout, TableLoadingRow, TwoLineTableCell } from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";
import { AgoFormat } from "VSSUI/Utilities/Date";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";

export class ChangesetsTable extends VssComponent<IChangesetsTableProps> {
    private commentColumn: ITableColumn<IChangeset> = {
        columnLayout: TableColumnLayout.twoLine,
        id: "comment",
        renderCell: (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<IChangeset>, tableItem: IChangeset) => {
            const shortComment = getShortCommitComment(tableItem.comment, 250);
            const truncatedComment = renderTooltipWithBreaks(
                tableItem.commentTruncated && tableItem.comment ? tableItem.comment.trim() + "..." : tableItem.comment
            );

            const line1 = (
                <Tooltip overflowOnly={shortComment === tableItem.comment} renderContent={truncatedComment ? () => truncatedComment : undefined}>
                    <div className="text-ellipsis font-weight-semibold flex-grow">{shortComment}</div>
                </Tooltip>
            );

            const line2 = (
                <div className="flex-row flex-center body-s secondary-text rhythm-horizontal-8 scroll-hidden">
                    <div className="flex-row flex-center scroll-hidden">
                        <Tooltip overflowOnly={true}>
                            <span className="text-ellipsis monospaced-xs">{tableItem.changesetId}</span>
                        </Tooltip>
                    </div>
                    <div className="flex-row flex-center scroll-hidden rhythm-horizontal-4">
                        <Persona identity={tableItem.owner} size={PersonaSize.size16} />
                        <Tooltip overflowOnly={true}>
                            <div className="text-ellipsis" aria-hidden="true">{tableItem.owner.displayName}</div>
                        </Tooltip>
                    </div>
                    <div className="flex-row flex-center scroll-hidden">
                        <Ago date={tableItem.date} format={AgoFormat.Extended} />
                    </div>
                </div>
            );

            return (
                <TwoLineTableCell
                    className="repos-changesets-table-content"
                    columnIndex={columnIndex}
                    key={columnIndex}
                    tableColumn={tableColumn}
                    line1={line1}
                    line2={line2}
                />
            );
        },
        minWidth: 200,
        name: Resources.ChangesetHeader,
        width: new ObservableValue(-100)
    };

    public render() {
        const tableColumns: ITableColumn<IChangeset>[] = [this.commentColumn];

        if (this.props.showChangeType) {
            tableColumns.push(changeTypeColumn);
        }

        return (
            <Table<IChangeset>
                className={css(this.props.className, "repos-changesets-table")}
                columns={tableColumns}
                itemProvider={new ArrayItemProvider(this.props.changesets)}
                onActivate={(event: React.SyntheticEvent<HTMLElement>, tableRow: ITableRow<IChangeset>) => {
                    if (this.props.onActivate) {
                        this.props.onActivate(event, tableRow.data);
                    }
                    if (!event.defaultPrevented) {
                        onClickFPS(this.context.pageContext, tableRow.data.linkProps.href!, true, event);
                    }
                }}
                selectableText={true}
                showHeader={!!this.props.showHeader}
                spacerWidth={this.props.spacerWidth}
                renderLoadingRow={(rowIndex: number, rowDetails: ITableRowDetails<IChangeset>) => {
                    return (
                        <Observer dataLoaded={this.props.dataLoaded}>
                            {(props: { dataLoaded: boolean }) => {
                                props.dataLoaded && this.props.loadMore && this.props.loadMore();
                                return <TableLoadingRow columns={tableColumns} details={rowDetails} rowIndex={rowIndex} />;
                            }}
                        </Observer>
                    );
                }}
            />
        );
    }
}

Components.add("repos-changesets-table", ChangesetsTable);

const changeTypeColumn: ITableColumn<IChangeset> = {
    id: "changeType",
    renderCell: (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<IChangeset>, tableItem: IChangeset) => {
        return (
            <SimpleTableCell columnIndex={columnIndex} key={columnIndex} tableColumn={tableColumn}>
                {getChangeTypeDisplayName(tableItem.changeType)}
            </SimpleTableCell>
        );
    },
    name: Resources.ChangeTypeHeader,
    width: new ObservableValue(100)
};

function renderTooltipWithBreaks(text?: string) {
    const result = text && (
        <div>
            {text.split("\n").map((str, index) => (
                <p key={index}>{str}</p>
            ))}
        </div>
    );
    return result;
}
